import { CartridgeExportData } from "./exportCartridge";
import { create } from "xmlbuilder2";
import { zipSync } from "fflate";
import { marked } from "marked";
import { v4 as uuidv4 } from "uuid";

interface Lesson {
  id: string;
  title: string;
  type: "html" | "external_url" | "discussion" | "assignment" | "lti_link";
  content?: string;
  description?: string;
  url?: string;
  ltiUrl?: string;
  ltiCustomProperties?: Array<{
    name: string;
    value: string;
  }>;
}

interface Chapter {
  id: string;
  title: string;
  lessons: Lesson[];
}

interface Standard {
  id: string;
  title: string;
  description: string;
  chapters: Chapter[];
}

export async function exportCartridgeFrontend(
  courseData: CartridgeExportData
): Promise<{ blob: Blob; filename: string }> {
  // This object will hold all files to be zipped
  const filesToZip: { [key: string]: Uint8Array } = {};
  const textEncoder = new TextEncoder();

  // Build the IMSMANIFEST.XML structure
  const manifestIdentifier = `${uuidv4()}`;
  const courseIdentifier = `ims-course-${uuidv4()}`;

  const root = create({ version: "1.0", encoding: "UTF-8" }).ele("manifest", {
    identifier: manifestIdentifier,
    xmlns: "http://www.imsglobal.org/xsd/imsccv1p1/imscp_v1p1",
    "xmlns:lom": "http://ltsc.ieee.org/xsd/imsccv1p1/LOM/resource",
    "xmlns:lomimscc": "http://ltsc.ieee.org/xsd/imsccv1p1/LOM/manifest",
    "xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
    "xsi:schemaLocation":
      "http://www.imsglobal.org/xsd/imsccv1p1/imscp_v1p1 http://www.imsglobal.org/profile/cc/ccv1p1/ccv1p1_imscp_v1p2_v1p0.xsd http://ltsc.ieee.org/xsd/imsccv1p1/LOM/resource http://www.imsglobal.org/profile/cc/ccv1p1/LOM/ccv1p1_lomresource_v1p0.xsd http://ltsc.ieee.org/xsd/imsccv1p1/LOM/manifest http://www.imsglobal.org/profile/cc/ccv1p1/LOM/ccv1p1_lommanifest_v1p0.xsd",
  });

  root
    .ele("metadata")
    .ele("schema")
    .txt("IMS Common Cartridge")
    .up()
    .ele("schemaversion")
    .txt("1.1.0")
    .up()
    .ele("lom:lom")
    .ele("lom:general")
    .ele("lom:title")
    .ele("lom:string")
    .txt(courseData.courseTitle);

  const orgs = root.ele("organizations").ele("organization", {
    identifier: courseIdentifier,
    structure: "hierarchical",
  });
  orgs.ele("title").txt(courseData.courseTitle);
  const resources = root.ele("resources");

  // Process the hierarchy and generate resources
  for (const standard of courseData.standards) {
    const standardItem = orgs.ele("item", {
      identifier: `item-std-${standard.id}`,
    });
    standardItem.ele("title").txt(standard.title);

    for (const chapter of standard.chapters) {
      const chapterItem = standardItem.ele("item", {
        identifier: `item-ch-${chapter.id}`,
      });
      chapterItem.ele("title").txt(chapter.title);

      for (const lesson of chapter.lessons) {
        const resourceIdentifier = `res-${lesson.id}`;

        chapterItem
          .ele("item", {
            identifier: `item-lesson-${lesson.id}`,
            identifierref: resourceIdentifier,
          })
          .ele("title")
          .txt(lesson.title);

        // Define the resource based on its type
        if (lesson.type === "html") {
          const fileName = `content/${lesson.id}.html`;
          const resource = resources.ele("resource", {
            identifier: resourceIdentifier,
            type: "webcontent",
            href: fileName,
          });
          resource.ele("file", { href: fileName });

          // Convert markdown to HTML before encoding
          const htmlContent = await marked(lesson.content || "");
          filesToZip[fileName] = textEncoder.encode(htmlContent);
        } else if (lesson.type === "external_url") {
          const xmlFileName = `links/${lesson.id}.xml`;
          resources
            .ele("resource", {
              identifier: resourceIdentifier,
              type: "imswl_xmlv1p1",
            })
            .ele("file", { href: xmlFileName });

          const webLinkXml = create({ version: "1.0", encoding: "UTF-8" })
            .ele("webLink", {
              xmlns: "http://www.imsglobal.org/xsd/imswl_v1p1",
            })
            .ele("title")
            .txt(lesson.title)
            .up()
            .ele("url", { href: lesson.url || "#", target: "_blank" })
            .up()
            .end({ prettyPrint: true });

          filesToZip[xmlFileName] = textEncoder.encode(webLinkXml);
        } else if (lesson.type === "discussion") {
          const xmlFileName = `discussions/${lesson.id}.xml`;
          resources
            .ele("resource", {
              identifier: resourceIdentifier,
              type: "imsdt_xmlv1p1",
            })
            .ele("file", { href: xmlFileName });

          const discussionXml = create({ version: "1.0", encoding: "UTF-8" })
            .ele("topic", {
              xmlns: "http://www.imsglobal.org/xsd/imsdt_v1p1",
            })
            .ele("title")
            .txt(lesson.title)
            .up()
            .ele("text", { texttype: "text/html" })
            .txt(lesson.description || "")
            .up()
            .end({ prettyPrint: true });

          filesToZip[xmlFileName] = textEncoder.encode(discussionXml);
        } else if (lesson.type === "assignment") {
          const xmlFileName = `assignments/${lesson.id}.xml`;
          resources
            .ele("resource", {
              identifier: resourceIdentifier,
              type: "imsas_xmlv1p1",
            })
            .ele("file", { href: xmlFileName });

          const assignmentXml = create({ version: "1.0", encoding: "UTF-8" })
            .ele("assignment", {
              xmlns: "http://www.imsglobal.org/xsd/imsas_v1p1",
            })
            .ele("title")
            .txt(lesson.title)
            .up()
            .ele("text", { texttype: "text/html" })
            .txt(lesson.description || "")
            .up()
            .end({ prettyPrint: true });

          filesToZip[xmlFileName] = textEncoder.encode(assignmentXml);
        } else if (lesson.type === "lti_link") {
          const xmlFileName = `lti_links/${lesson.id}.xml`;
          resources
            .ele("resource", {
              identifier: resourceIdentifier,
              type: "imsbasiclti_xmlv1p0",
            })
            .ele("file", { href: xmlFileName });

          const ltiXml = create({ version: "1.0", encoding: "UTF-8" })
            .ele("cartridge_basiclti_link", {
              xmlns: "http://www.imsglobal.org/xsd/imslticc_v1p3",
              "xmlns:blti": "http://www.imsglobal.org/xsd/imsbasiclti_v1p0",
              "xmlns:lticm": "http://www.imsglobal.org/xsd/imslticm_v1p0",
              "xmlns:lticp": "http://www.imsglobal.org/xsd/imslticp_v1p0",
              "xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
              "xsi:schemaLocation":
                "http://www.imsglobal.org/xsd/imslticc_v1p3.xsd http://www.imsglobal.org/xsd/imslticp_v1p0 imslticp_v1p0.xsd http://www.imsglobal.org/xsd/imslticm_v1p0 imslticm_v1p0.xsd http://www.imsglobal.org/xsd/imsbasiclti_v1p0 imsbasiclti_v1p0p1.xsd",
            })
            .ele("blti:title")
            .txt("PFL-CANVAS")
            .up()
            .ele("blti:description")
            .txt("test")
            .up()
            .ele("blti:secure_launch_url")
            .txt(`${import.meta.env.VITE_API_BASE_URL}/lti`)
            .up()
            .ele("blti:custom");

          // Add custom properties
          if (lesson.ltiCustomProperties) {
            lesson.ltiCustomProperties.forEach((prop) => {
              ltiXml.ele("lticm:property", { name: prop.name }).txt(prop.value);
            });
          }

          // Close the custom element
          ltiXml.up();

          const ltiXmlString = ltiXml.end({ prettyPrint: true });
          filesToZip[xmlFileName] = textEncoder.encode(ltiXmlString);
        }
      }
    }
  }

  // Finalize and add the manifest file
  const imsmanifestXml = root.end({ prettyPrint: true });
  filesToZip["imsmanifest.xml"] = textEncoder.encode(imsmanifestXml);

  // Create the ZIP archive
  const zipData = zipSync(filesToZip, { level: 9 }); // Max compression

  // Create blob and return
  const blob = new Blob([zipData], {
    type: "application/vnd.ims.imsccv1p1+zip",
  });

  return {
    blob,
    filename: `${courseData.courseId || "course"}-export.imscc`,
  };
}

// Helper function to download the blob
export function downloadCartridge(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
